import React from 'react';
import { SectionTitle } from '../SectionTitle';
import PitchMatchGraph from '../../tinnitogram/PitchMatchGraph';
import LoudnessMatchGraph from '../../tinnitogram/LoudnessMatchGraph';
import MaskingCurveGraph from '../../tinnitogram/MaskingCurveGraph';
import {
  normaliseTinnitogram, derivePitchType, deriveLaterality, activeEarForSummary,
} from '../../tinnitogram/tinnitogramLogic';

/**
 * TinnitogramReportSection — compact renderer used by the standard
 * hearing-report layout. Falls back silently to the generic renderer
 * when the saved data only has the pre-rebuild `{fields: {...}}` bag.
 */
export const TinnitogramReportSection = ({ data }) => {
  const s = normaliseTinnitogram(data);
  const hasMaskingData = Object.keys(s.masking.right?.values || {}).length > 0
    || Object.keys(s.masking.left?.values || {}).length > 0;
  const hasNew = (s.pitch.right.match_freq || s.pitch.left.match_freq)
    || Object.keys(s.pitch.right.thresholds || {}).length > 0
    || Object.keys(s.pitch.left.thresholds  || {}).length > 0
    || hasMaskingData
    || s.loudness.right.loudness_db_sl !== null
    || s.clinical_interpretation || s.recommendations;
  if (!hasNew) return null;

  const ear = activeEarForSummary(s);
  const pitchMatch = s.pitch[ear];
  const loud = s.loudness[ear];

  return (
    <div data-testid="tinnitogram-report-section">
      <SectionTitle>Tinnitogram · Pitch / Loudness / Masking</SectionTitle>
      <div className="grid grid-cols-3 gap-1.5 mb-1">
        <div className="border border-slate-300 rounded p-1">
          <div className="text-[9px] font-bold text-sky-700 uppercase mb-0.5">Pitch Match</div>
          <PitchMatchGraph pitch={s.pitch} readOnly width={220} height={110} />
        </div>
        <div className="border border-slate-300 rounded p-1">
          <div className="text-[9px] font-bold text-sky-700 uppercase mb-0.5">Loudness</div>
          <LoudnessMatchGraph loudness={s.loudness} activeEar={ear} readOnly width={220} height={110} />
        </div>
        <div className="border border-slate-300 rounded p-1">
          <div className="text-[9px] font-bold text-sky-700 uppercase mb-0.5">Masking</div>
          <MaskingCurveGraph masking={s.masking} readOnly width={220} height={110} />
        </div>
      </div>
      <div className="text-[10px] mb-0.5" data-testid="tin-report-section-summary">
        <span className="font-bold text-blue-800 uppercase tracking-wide text-[9px] mr-1">Summary:</span>
        <span>{derivePitchType(pitchMatch.match_freq)}</span>
        {pitchMatch.match_freq && <span> · {pitchMatch.match_freq.toLocaleString()} Hz</span>}
        {loud.loudness_db_sl !== null && loud.loudness_db_sl !== undefined && <span> · {loud.loudness_db_sl} dB SL</span>}
        {loud.mml_db_sl !== null && loud.mml_db_sl !== undefined && <span> · MML {loud.mml_db_sl} dB SL</span>}
        <span> · {deriveLaterality(s.test_ear)}</span>
      </div>
      {s.clinical_interpretation && (
        <div className="text-[10px] mb-0.5">
          <span className="font-bold text-blue-800 uppercase tracking-wide text-[9px] mr-1">Interpretation:</span>
          <span className="whitespace-pre-wrap">{s.clinical_interpretation}</span>
        </div>
      )}
      {s.recommendations && (
        <div className="text-[10px]">
          <span className="font-bold text-blue-800 uppercase tracking-wide text-[9px] mr-1">Recommendations:</span>
          <span className="whitespace-pre-wrap">{s.recommendations}</span>
        </div>
      )}
    </div>
  );
};

export default TinnitogramReportSection;
